import { useCallback } from 'react';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useShowSaveToFolderModal } from '@/shared/features/quiz-folder';

import { type CTAScenario } from './useCTAScenario';
import { useDuplicateHandler } from './useDuplicateHandler';
import { useEditHandler } from './useEditHandler';
import { useEnterHandler } from './useEnterHandler';
import { useExportWorksheetHandler } from './useExportWorksheetHandler';
import { useFlashcardHandler } from './useFlashcardHandler';
import { useGoToLmsHandler } from './useGoToLmsHandler';
import { useLinkToClassroomHandler } from './useLinkToClassroomHandler';
import { useShareHandler } from './useShareHandler';
import { useUpgradePlanHandler } from './useUpgradePlanHandler';

export interface CTAHandlers {
  handleEnter: () => void;
  handleShare: () => void;
  handleDuplicate: () => void;
  handleEdit: () => void;
  handleGoToLms: () => void;
  handleDuplicateLocked: () => void;
  handleUpgradePlan: () => void;
  handleExportWorksheet: () => void;
  handleFlashcard: () => void;
  handleLinkToClassroom: () => void;
  /** 내 퀴즈를 다른 폴더(또는 기본 내 퀴즈)로 옮긴다. 데스크톱·모바일 그룹이 같은 인자로 연다. */
  handleMoveToFolder: () => void;
}

interface UseCTAHandlersParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
  scenario: CTAScenario;
}

/**
 * 퀴즈 상세 CTA 핸들러 묶음. 핸들러마다 훅이 따로 있고 여기서 한 번에 돌려준다.
 * 데스크톱·모바일 버튼 그룹이 같은 핸들러를 받는다.
 */
export const useCTAHandlers = ({
  quizId,
  quizData,
  scenario,
}: UseCTAHandlersParams): CTAHandlers => {
  const handleEnter = useEnterHandler({ quizData, scenario });
  const handleShare = useShareHandler({ quizData });
  const handleDuplicate = useDuplicateHandler({ quizId, quizData });
  const handleEdit = useEditHandler({ quizId, quizData });
  const handleGoToLms = useGoToLmsHandler({ quizId });
  const handleDuplicateLocked = useDuplicateHandler({
    quizId,
    quizData,
    locked: true,
  });
  const handleUpgradePlan = useUpgradePlanHandler({ quizData });
  const handleExportWorksheet = useExportWorksheetHandler({ quizData });
  const handleFlashcard = useFlashcardHandler({ quizId, quizData });
  const linkToClassroom = useLinkToClassroomHandler({ quizData });
  const handleLinkToClassroom = useCallback(() => {
    void linkToClassroom();
  }, [linkToClassroom]);
  const showSaveToFolderModal = useShowSaveToFolderModal();
  // `currentFolderId` 를 빠뜨리면 `내 퀴즈`(폴더 밖) 행이 안 뜬다 — 이동은 빼낼 폴더를 알아야 한다.
  const handleMoveToFolder = useCallback(
    () =>
      void showSaveToFolderModal({
        id: quizId,
        intent: 'move',
        currentFolderId: quizData.folderId,
        place: 'quiz_detail_cta',
        // 이동 버튼은 소유자에게만 있다.
        isQuizOwner: true,
      }),
    [showSaveToFolderModal, quizId, quizData.folderId],
  );

  return {
    handleEnter,
    handleShare,
    handleDuplicate,
    handleEdit,
    handleGoToLms,
    handleDuplicateLocked,
    handleUpgradePlan,
    handleExportWorksheet,
    handleFlashcard,
    handleLinkToClassroom,
    handleMoveToFolder,
  };
};
